var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { IconButton } from '@dixa/lib-shared-habla';
import { detect } from 'detect-browser';
import { useCallback, useEffect, useMemo, useState } from 'react';
import RecordRTC from 'recordrtc';
import { SoundMeter, } from 'components/conversation/components/soundMeter/soundMeter';
import { AudioPlayer } from '../audioPlayer/audioPlayer';
import styles from './recordPlayer.module.scss';
export const RecordPlayer = ({ onChange, name }) => {
    const [blobObjectURL, setBlobObjectURL] = useState(null);
    const [sound, setSound] = useState([]);
    const [isRecording, setIsRecording] = useState(false);
    const stream = useMemo(() => ({ file: null, recorder: null, mediaStream: null }), []);
    useEffect(() => void onChange(stream.file), [onChange, stream.file]);
    const onRecordClick = useCallback(() => setIsRecording((isRecording) => {
        const { recorder, mediaStream } = stream;
        if (isRecording && recorder && mediaStream) {
            recorder.stopRecording(() => {
                const tracks = mediaStream.getAudioTracks();
                const blob = recorder.getBlob();
                const blobObjectURL = URL.createObjectURL(blob);
                setBlobObjectURL(blobObjectURL);
                tracks.forEach((track) => track.stop());
                stream.file = new File([blob], name, { type: blob.type });
            });
        }
        if (!isRecording) {
            navigator.mediaDevices
                .getUserMedia({ audio: true })
                .then((mediaStream) => __awaiter(void 0, void 0, void 0, function* () {
                const BrowserAudioContext = Reflect.get(window, 'AudioContext') ||
                    Reflect.get(window, 'webkitAudioContext');
                const context = new BrowserAudioContext();
                const analyser = context.createAnalyser();
                const input = context.createMediaStreamSource(mediaStream);
                input.connect(analyser);
                analyser.smoothingTimeConstant = 0.2;
                analyser.fftSize = 64;
                const spectrum = new Uint8Array(analyser.fftSize);
                const browser = detect();
                const isChromium = ['chrome', 'edge-chromium'].includes((browser === null || browser === void 0 ? void 0 : browser.name) || '');
                const recorder = new RecordRTC(mediaStream, {
                    type: 'audio',
                    disableLogs: true,
                    mimeType: 'audio/wav',
                    recorderType: isChromium
                        ? RecordRTC.StereoAudioRecorder
                        : RecordRTC.MediaStreamRecorder,
                    /*
                     * Record in chunks of 500ms.
                     *
                     * This is so that our 'ondataavailable'
                     * handler would get called every so often.
                     */
                    timeSlice: 500,
                    ondataavailable: () => {
                        analyser.getByteFrequencyData(spectrum);
                        const rms = Math.sqrt(spectrum.reduce((acc, val) => acc + Math.pow(val, 2), 0) /
                            spectrum.length);
                        setSound((sound) => [
                            ...sound,
                            {
                                output: 0,
                                /*
                                 * If input falls down to 0 then we won't see a bar anymore,
                                 * so we use minimum of 0.2 to still show a bar even when silent.
                                 */
                                input: Math.max(rms / 100, 0.2),
                                isRecording: true,
                            },
                        ]);
                    },
                });
                recorder.startRecording();
                stream.mediaStream = mediaStream;
                stream.recorder = recorder;
            }));
        }
        return !isRecording;
    }), [name, setIsRecording, stream]);
    const onDeleteClick = useCallback(() => {
        stream.mediaStream = null;
        stream.recorder = null;
        stream.file = null;
        setSound([]);
        setBlobObjectURL(null);
    }, [stream, setSound, setBlobObjectURL]);
    return blobObjectURL ? (_jsx(_Fragment, { children: _jsx(AudioPlayer, { isDownloadable: true, src: blobObjectURL, filename: name, onDelete: onDeleteClick }) })) : (_jsxs("div", Object.assign({ className: styles.record }, { children: [_jsx(IconButton, { variant: "secondary", icon: isRecording ? 'stop' : 'record', onClick: onRecordClick }), _jsx("div", Object.assign({ className: styles.recordStatus }, { children: !isRecording ? 'Start recording' : _jsx(SoundMeter, { sounds: sound }) })), _jsx(IconButton, { variant: "secondary", icon: "trash", disabled: true })] })));
};
